// frontend/src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import animations
import './styles/animations.css';

// Import Leaflet CSS for Indoor Mapping
import 'leaflet/dist/leaflet.css';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { FileProvider } from './contexts/FileContext';
import { CombinedThemeProvider } from './contexts/ThemeContext';
import { DeviceProvider } from './contexts/DeviceContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { SiteFilterProvider } from './contexts/SiteFilterContext';

// Notification components
import NotificationPrompt from './components/NotificationPrompt';
import DesktopNotificationOverlay from './components/DesktopNotificationOverlay';
import notificationService from './services/notificationService';

// Layout Components
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import PermissionRoute from './components/PermissionRoute';
import CompanyPermissionRoute from './components/CompanyPermissionRoute';
import InstallPWA from './components/InstallPWA';

// Pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import AuthError from './pages/AuthError';
import PermissionDenied from './pages/PermissionDenied';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import Health from './pages/Health';
import TicketManagementSystem from './pages/Tickets';
import TicketsManagement from './pages/TicketsManagement';
import LocationManagement from './pages/LocationManagement/index';
import DeviceManagement from './pages/DeviceManagement/indexOptimized';
import UserManagement from './pages/UserManagement';
import Help from './pages/Help';
import ReleaseNotesViewer from './pages/ReleaseNotesViewer';
import DeveloperInfo from './pages/DeveloperInfo';
import CameraRequests from './pages/CameraRequests/index';
import RequestDetail from './pages/CameraRequests/RequestDetail';
import CompanyManagement from './pages/CompanyManagement/index';
import FieldInterventions from './pages/FieldInterventions/index';
import InterventionDetail from './pages/FieldInterventions/InterventionDetailResponsive';
import CreateIntervention from './pages/FieldInterventions/CreateIntervention';
import Notifications from './pages/Notifications';
import NotificationManagement from './pages/NotificationManagement';
import IntegrationManagement from './pages/IntegrationManagement';
import EmergencyServicesManagement from './pages/EmergencyServicesManagement';
import CommanderList from './components/Integrations/TwoN/CommanderList';
import FirmwareManagement from './pages/FirmwareManagement';
import AlarmsDashboard from './pages/AlarmsDashboard';
import AlarmDetails from './pages/AlarmDetails';
import IndoorMapping from './pages/IndoorMapping';
import IndoorNavigationPreview from './pages/IndoorNavigationPreview';
import ServiceManagement from './pages/ServiceManagement';
import DeviceTypeManagement from './pages/DeviceTypeManagement';
import DeviceLinking from './pages/DeviceLinking';
import BaseIPManagement from './pages/BaseIPManagement';
import BusinessUpdate from './pages/BusinessUpdate';
import SalesPresentation from './pages/SalesPresentation';
import MonitoringDashboard from './pages/MonitoringDashboard';
import PersonsManagement from './pages/PersonsManagement';
import PersonTypeManagement from './pages/PersonTypeManagement';
import EnOceanButtonManagement from './pages/EnOceanButtonManagement';
import QbusOutputTypeManagement from './pages/QbusOutputTypeManagement';
import QbusAutomations from './pages/QbusAutomations';
import QbusFriendlyNames from './pages/QbusFriendlyNames';
import TelephonyManagement from './pages/TelephonyManagement';
import EinkDesigner from './pages/EinkDesigner';
// import TranslationManagement from './pages/TranslationManagement/index';

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  useEffect(() => {
    // Initialize notification service
    notificationService.initialize();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CombinedThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <SnackbarProvider maxSnack={3} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
            <AuthProvider>
              <PermissionProvider>
                <FileProvider>
                  <DeviceProvider>
                    <NavigationProvider>
                      <SiteFilterProvider>
                      <Router>
                        {/* PWA Install Prompt */}
                        <InstallPWA />

                        {/* Notification Permission Prompt */}
                        <NotificationPrompt />

                        {/* Desktop Notification Overlay (for PWA) */}
                        <DesktopNotificationOverlay />

                        <Routes>
                          {/* Public routes */}
                          <Route path="/login" element={<Login />} />
                          <Route path="/health" element={<Health />} />

                          {/* Public alarm access via UUID (no auth required) */}
                          {/* Supports both /alarm/:uuid and /alarms/:uuid for flexibility */}
                          <Route path="/alarm/:uuid" element={<AlarmDetails />} />
                          <Route path="/alarms/uuid/:uuid" element={<AlarmDetails />} />

                          {/* Auth Callback Routes */}
                          <Route path="/auth-callback" element={<AuthCallback />} />
                          <Route path="/auth-error" element={<AuthError />} />
                          <Route path="/permission-denied" element={<PermissionDenied />} />

                          {/* Private routes */}
                          <Route element={<PrivateRoute />}>
                            <Route element={<Layout />}>
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                              <Route path="/dashboard" element={<Dashboard />} />


                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="locations.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/locations" element={<LocationManagement />} />
                            </Route>

                            <Route element={<CompanyPermissionRoute
                              application="API-management-portal"
                              permission="devices.view"
                              allowedCompanyIds={1}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/devices" element={<DeviceManagement />} />
                            </Route>

                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="tickets.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/tickets" element={<TicketsManagement />} />
                              <Route path="/tickets-new" element={<TicketsManagement />} />
                              <Route path="/tickets-old" element={<TicketManagementSystem />} />
                            </Route>

                            {/* Alarms Dashboard - Admin only */}
                            <Route path="/alarms" element={<AlarmsDashboard />} />
                            <Route path="/alarms/:id" element={<AlarmDetails />} />

                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="camera_requests.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/camera-requests" element={<CameraRequests />} />
                              <Route path="/camera-requests/:id" element={<RequestDetail />} />
                            </Route>

                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="field_interventions.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/field-interventions" element={<FieldInterventions />} />
                              <Route path="/field-interventions/new" element={<CreateIntervention />} />
                              <Route path="/field-interventions/:id" element={<InterventionDetail />} />
                            </Route>

                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/users" element={<UserManagement />} />
                            </Route>

                            {/* Persons Management */}
                            <Route element={<PermissionRoute
                              application="API-portal"
                              permission="persons.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/persons" element={<PersonsManagement />} />
                            </Route>

                            {/* Person Type Management (admin) */}
                            <Route element={<PermissionRoute
                              application="API-portal"
                              permission="persontypes.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/person-type-management" element={<PersonTypeManagement />} />
                            </Route>

                            {/* Admin-only routes - admin check is handled within the components */}
                            <Route path="/company-management" element={<CompanyManagement />} />
                            <Route path="/service-management" element={<ServiceManagement />} />
                            <Route path="/device-type-management" element={<DeviceTypeManagement />} />
                            <Route path="/device-linking" element={<DeviceLinking />} />
                            <Route path="/baseip-management" element={<BaseIPManagement />} />
                            <Route path="/enocean-buttons" element={<EnOceanButtonManagement />} />
                            <Route path="/qbus-output-types" element={<QbusOutputTypeManagement />} />
                            <Route path="/qbus-automations" element={<QbusAutomations />} />
                            <Route path="/qbus-friendly-names" element={<QbusFriendlyNames />} />
                            <Route path="/telephony" element={<TelephonyManagement />} />
                            <Route path="/eink-designer" element={<EinkDesigner />} />

                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/integration-management" element={<IntegrationManagement />} />
                              <Route path="/admin/integrations/emergency-services" element={<EmergencyServicesManagement />} />
                              <Route path="/admin/integrations/twon/commanders" element={<CommanderList />} />
                            </Route>

                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission={["devices.edit", "devices.create"]}
                              requireAll={false}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/firmware-management" element={<FirmwareManagement />} />
                            </Route>

                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/notification-management" element={<NotificationManagement />} />
                            </Route>

                            {/* <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/translations" element={<TranslationManagement />} />
                            </Route> */}

                            <Route path="/settings" element={<Settings />} />
                            <Route path="/notifications" element={<Notifications />} />
                            <Route path="/monitoring" element={<MonitoringDashboard />} />
                            <Route path="/help" element={<Help />} />
                            <Route path="/release-notes/:version" element={<ReleaseNotesViewer />} />

                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/developer-info" element={<DeveloperInfo />} />
                            </Route>

                            {/* Indoor Mapping Admin - Admin only */}
                            <Route element={<PermissionRoute
                              application="API-management-portal"
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/indoor-mapping" element={<IndoorMapping />} />
                              <Route path="/indoor-navigation-preview" element={<IndoorNavigationPreview />} />
                            </Route>

                            </Route>

                          {/* Fullscreen presentations - outside Layout for no navbar/header/footer */}
                          <Route element={<PermissionRoute
                            application="API-management-portal"
                            permission="users.permissions.manage"
                            fallbackPath="/permission-denied"
                          />}>
                            <Route path="/business-update" element={<BusinessUpdate />} />
                            <Route path="/sales-presentation" element={<SalesPresentation />} />
                          </Route>
                          </Route>

                          {/* Catch-all - redirect to login */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Router>
                      </SiteFilterProvider>
                    </NavigationProvider>
                  </DeviceProvider>
                </FileProvider>
              </PermissionProvider>
            </AuthProvider>
          </SnackbarProvider>
        </LocalizationProvider>
      </CombinedThemeProvider>
    </QueryClientProvider>
  );
}

export default App;